import config from '../../../config';
import { classifyCartItem } from './classifiers';
import {
  DEFAULT_EXCLUDED_COMPOSITION_TYPES,
  DEFAULT_PRODUCT_TYPE_DISCOUNT_RULES,
  REGULAR_DISCOUNT_EXCLUDED_COMPOSITION_TYPES,
} from './constants';

export function sumGiftsForBonuses(composition = []) {
  return composition.reduce(
    (summ, item) => (item.type === 'bonus' ? summ + item.price * item.count : summ),
    0,
  );
}

export function calculateUnusedBonuses(balance, usedBonuses, giftsForBonusesSum) {
  return balance - usedBonuses - giftsForBonusesSum;
}

export function calculateMaxBonusesMinusGifts(balance, giftsForBonusesSum) {
  return balance - giftsForBonusesSum;
}

function hasDepositIngredient(item) {
  return (
    Array.isArray(item?.item?.added_ingredients) &&
    item.item.added_ingredients.some(ing => ing?.slug === 'kaucja' || ing?.type === 'Deposit')
  );
}

function getCompositionLineTotal(item) {
  const hasDeposit = hasDepositIngredient(item);
  const unitPriceForDiscount = hasDeposit
    ? Number(item?.item?.price || 0)
    : Number(item?.price || 0);
  const qty = Number(item?.count ?? item?.item?.count ?? 1);

  return unitPriceForDiscount * qty;
}

function isEligibleByProductRule(
  cartItem,
  ruleKey,
  { rules = DEFAULT_PRODUCT_TYPE_DISCOUNT_RULES } = {},
) {
  if (REGULAR_DISCOUNT_EXCLUDED_COMPOSITION_TYPES.includes(cartItem?.type)) {
    return false;
  }

  const { productType } = classifyCartItem(cartItem);

  return Boolean(rules[productType]?.[ruleKey]);
}

function calculateRegularEligibleCompositionTotal(
  composition = [],
  ruleKey,
  options = {},
) {
  return composition
    .filter(item => isEligibleByProductRule(item, ruleKey, options))
    .reduce((sum, item) => sum + getCompositionLineTotal(item), 0);
}

export function isEligibleForPickupDiscount(cartItem, options = {}) {
  return isEligibleByProductRule(cartItem, 'eligibleForPickup', options);
}

export function isEligibleForBonuses(cartItem, options = {}) {
  return isEligibleByProductRule(cartItem, 'eligibleForBonuses', options);
}

/**
 * Pickup discount applies only to regular (non-combo, non-promotional) cart lines.
 */
export function calculatePickupEligibleCompositionTotal(
  composition = [],
  options = {},
) {
  return calculateRegularEligibleCompositionTotal(composition, 'eligibleForPickup', options);
}

/**
 * Bonus spend limits apply only to regular (non-combo, non-promotional) cart lines.
 */
export function calculateBonusesEligibleCompositionTotal(
  composition = [],
  options = {},
) {
  return calculateRegularEligibleCompositionTotal(composition, 'eligibleForBonuses', options);
}

/**
 * Eligible composition total for layered discounts (pickup discount today).
 * Bonus spend eligibility will use the same hook when backend rules arrive.
 */
export function calculateEligibleCompositionTotal(
  composition = [],
  { excludeTypes = DEFAULT_EXCLUDED_COMPOSITION_TYPES } = {},
) {
  return composition
    .filter(p => !excludeTypes.includes(p?.type))
    .reduce((sum, p) => sum + getCompositionLineTotal(p), 0);
}

export function calculatePickupDiscountValue(pickupBonuses, eligiblePrice) {
  if (!pickupBonuses || pickupBonuses.reward !== 'order_discount') {
    return 0;
  }

  if (pickupBonuses.type === 'percent') {
    return (eligiblePrice * Number(pickupBonuses.reward_value || 0)) / 100;
  }

  return Math.min(Number(pickupBonuses.reward_value || 0), eligiblePrice);
}

export function formatPickupDiscountForDisplay(pickupDiscountValue, legal = config.legal) {
  if (legal === 'by' || legal === 'pl') {
    return pickupDiscountValue;
  }

  return Math.floor(pickupDiscountValue);
}

export function canApplyPickupDiscount(pickupBonuses, stockCode) {
  return pickupBonuses?.reward === 'order_discount' && !stockCode;
}

/**
 * Pickup totals for Apple Pay and other flows that need pickup pricing
 * without relying on order.checkout.deliveryType.
 */
export function calculatePickupPricingTotals({
  usedBonuses = 0,
  stockCode,
  price = 0,
  pickupBonuses = {},
  composition = [],
  legal = config.legal,
}) {
  const isPickupDiscountApplied = canApplyPickupDiscount(pickupBonuses, stockCode);

  if (!isPickupDiscountApplied) {
    return {
      total: null,
      pickupDiscountValue: 0,
      isPickupDiscountApplied: false,
    };
  }

  const eligiblePrice = calculatePickupEligibleCompositionTotal(composition);
  const rawDiscountValue = calculatePickupDiscountValue(pickupBonuses, eligiblePrice);
  const pickupDiscountValue = formatPickupDiscountForDisplay(rawDiscountValue, legal);
  const total = normalizeCheckoutPrice(
    Math.max(0, Number(price) - rawDiscountValue - Number(usedBonuses || 0)),
    legal,
  );

  return {
    total,
    pickupDiscountValue,
    isPickupDiscountApplied: true,
  };
}

export function normalizeCheckoutPrice(currentPrice, legal = config.legal) {
  const result =
    legal === 'by' || legal === 'pl' ? Number(currentPrice) : Math.floor(Number(currentPrice));

  return result <= 0 ? 0 : result;
}

export function calculateMinOrderSum({
  minOrderSum = 0,
  minStockSum,
  stockIsApplied,
  stockIsChecked,
}) {
  let minSum = minOrderSum || 0;

  if (stockIsApplied && stockIsChecked && (minStockSum === 0 || minStockSum === 1)) {
    minSum = minStockSum;
  }

  return minSum;
}

export function calculateMaxCashbackCanUse({
  discountPrice = 0,
  discountPercent = 100,
  maxPointsSpend,
  minOrderSum = 0,
}) {
  const bonusesEdge = Math.floor((discountPrice * discountPercent) / 100);
  const min = Math.min(
    bonusesEdge,
    discountPrice - minOrderSum <= 0 ? 0 : Math.floor(discountPrice - minOrderSum),
  );

  return maxPointsSpend !== undefined ? Math.min(min, maxPointsSpend) : min;
}

export function calculatePriceIncludingBonuses({
  deliveryType,
  usedBonuses = 0,
  stockCode,
  price = 0,
  discountPrice = 0,
  pickupBonuses = {},
  composition = [],
  legal = config.legal,
}) {
  let currentPrice = Number(discountPrice) - Number(usedBonuses || 0);

  if (deliveryType === 'pickup' && pickupBonuses?.reward === 'order_discount' && !stockCode) {
    const eligiblePrice = calculatePickupEligibleCompositionTotal(composition);
    const discountValue = calculatePickupDiscountValue(pickupBonuses, eligiblePrice);

    currentPrice = Math.max(0, Number(price) - discountValue - Number(usedBonuses || 0));
  }

  return normalizeCheckoutPrice(currentPrice, legal);
}

/**
 * Single entry point for checkout pricing derived state.
 */
export function calculatePricing({
  deliveryType,
  usedBonuses = 0,
  stockCode,
  price = 0,
  discountPrice = 0,
  pickupBonuses = {},
  composition = [],
  loyaltyBalance = 0,
  discountPercent = 100,
  maxPointsSpend,
  minOrderSum = 0,
  legal = config.legal,
}) {
  const giftsForBonusesSum = sumGiftsForBonuses(composition);
  const eligibleForBonusesTotal = calculateBonusesEligibleCompositionTotal(composition);

  const total = calculatePriceIncludingBonuses({
    deliveryType,
    usedBonuses,
    stockCode,
    price,
    discountPrice,
    pickupBonuses,
    composition,
    legal,
  });

  const maxCashbackCanUse = calculateMaxCashbackCanUse({
    discountPrice: eligibleForBonusesTotal,
    discountPercent,
    maxPointsSpend,
    minOrderSum,
  });

  const maxBonusesMinusGifts = calculateMaxBonusesMinusGifts(loyaltyBalance, giftsForBonusesSum);
  const unusedBonuses = calculateUnusedBonuses(loyaltyBalance, usedBonuses, giftsForBonusesSum);
  const maxSpendableBonuses = Math.min(maxBonusesMinusGifts, maxCashbackCanUse);

  return {
    total,
    giftsForBonusesSum,
    maxBonusesMinusGifts,
    unusedBonuses,
    maxCashbackCanUse,
    maxSpendableBonuses,
    minOrderSum,
    eligibleForBonusesTotal,
  };
}
